import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import styles from './RechargeRecordModal.module.css';

const AVATAR_COLORS = ['#1677ff', '#7257e8', '#ff9d27', '#e5484d', '#20a9b5'];

const colorOfName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = hash * 31 + name.charCodeAt(i);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};


type RechargeRecord = {
  id: number;
  orderNo: string;
  amount: number;
  amountAfter: number;
  type: number;
  description: string;
  adminName: string;
  createdByName: string;
  createdAt: string;
};

const PERIOD_LABEL: Record<number, string> = {
  1: '今日',
  2: '昨日',
  3: '近七天',
  4: '本月',
  5: '上月',
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: number;
};

const formatYuan = (n: number) =>
  `¥${Number(n || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const RechargeRecordModal = ({ open, onOpenChange, period }: Props) => {
  const [records, setRecords] = useState<RechargeRecord[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPage<RechargeRecord>(
        `/admin/tel/recharge/records?period=${period}&limit=100`
      );
      setRecords(result.records);
      setTotalAmount(result.records.reduce((sum, item) => sum + (Number(item.amount) || 0), 0));
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (open) {
      fetchRecords();
    }
  }, [open, fetchRecords]);

  const label = PERIOD_LABEL[period] ?? '';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>{label}充值记录</div>
          <div className={styles.bannerSub}>
            共 {records.length} 笔，合计 {formatYuan(totalAmount)}
          </div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : records.length > 0 ? (
          <div className={styles.list}>
            {records.map((record) => {
              const name = record.adminName || `代理 #${record.id}`;
              return (
                <div key={record.id} className={styles.item}>
                  <span
                    className={styles.avatar}
                    style={{ background: colorOfName(name) }}
                  >
                    {firstChineseChar(name)}
                  </span>
                  <div className={styles.itemMain}>
                    <div className={styles.itemTop}>
                      <span className={styles.name}>{name}</span>
                      <span className={styles.amount}>+{formatYuan(record.amount)}</span>
                    </div>
                    <div className={styles.itemBottom}>
                      <span className={styles.time}>{record.createdAt}</span>
                      <span className={styles.after}>余额 {formatYuan(record.amountAfter)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty}>暂无充值记录</div>
        )}
      </div>
    </Modal>
  );
};
